import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  api,
  bootstrapSession,
  clearSessionTokens,
  setSessionExpiredHandler,
  setSessionTokens,
  verificationRequiredFrom,
} from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  // status drives the route guards: "loading" | "authenticated" | "anonymous".
  // Never treat a truthy-looking object as a session without a validated token.
  const [status, setStatus] = useState("loading");
  const [user, setUser] = useState(null);
  const loading = status === "loading";

  const signOutLocal = useCallback(() => {
    clearSessionTokens();
    setUser(null);
    setStatus("anonymous");
  }, []);

  // Bootstrap: validate the persisted JWT before rendering the app shell.
  // A 200 here means the backend verified the token; anything else is anonymous.
  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (!data || (!data._id && !data.id) || !data.email) {
        signOutLocal();
        return null;
      }
      setUser(data);
      setStatus("authenticated");
      return data;
    } catch {
      signOutLocal();
      return null;
    }
  }, [signOutLocal]);

  // On first load (or a browser refresh) the tokens come back from localStorage.
  // Expired access tokens are exchanged for a fresh pair before /auth/me runs.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await bootstrapSession();
      if (cancelled) return;
      if (ok) {
        await fetchMe();
        return;
      }
      signOutLocal();
    })();
    return () => { cancelled = true; };
  }, [fetchMe, signOutLocal]);

  // If an interceptor exhausts the refresh token, drop to anonymous so the
  // guards bounce the user to /login instead of leaving a dead shell open.
  useEffect(() => {
    setSessionExpiredHandler(signOutLocal);
  }, [signOutLocal]);

  const login = useCallback(
    async (email, password, guard = {}) => {
      try {
        const { data } = await api.post("/auth/login", {
          ...guard,
          email,
          password,
        });
        if (!data?.access_token) {
          throw new Error("Login did not return a session token");
        }
        setSessionTokens(data);
        // Confirm the token against the API before granting access to /app.
        const me = await fetchMe();
        if (!me) {
          clearSessionTokens();
          throw new Error("Could not verify the session. Please try again.");
        }
        return me;
      } catch (err) {
        // An unverified account is not an error the user should just stare at;
        // surface the structured detail so the login page can route to /verify-email.
        const verification = verificationRequiredFrom(err);
        if (verification) {
          const redirect = new Error(
            verification.message || "Verify your email to continue.",
          );
          redirect.requiresVerification = true;
          redirect.email = verification.email || email;
          redirect.retryAfter = verification.retry_after;
          throw redirect;
        }
        throw err;
      }
    },
    [fetchMe],
  );

  const register = useCallback(
    async (name, email, password, consent = {}, guard = {}) => {
      // Registration no longer creates a session. The account exists but stays
      // locked until the emailed OTP is confirmed through verifyOtp().
      const { data } = await api.post("/auth/register", {
        ...guard,
        name,
        email,
        password,
        accepted_terms: true,
        allow_admin_data_view: Boolean(consent.allowView),
        allow_admin_data_modify: Boolean(consent.allowModify),
      });
      return data;
    },
    [],
  );

  const verifyOtp = useCallback(
    async (email, otp, guard = {}) => {
      const { data } = await api.post("/auth/verify-otp", {
        ...guard,
        email,
        otp,
      });
      if (!data?.access_token) {
        throw new Error("Verification did not return a session token");
      }
      setSessionTokens(data);
      const me = await fetchMe();
      if (!me) {
        clearSessionTokens();
        throw new Error("Could not verify the session. Please try again.");
      }
      return me;
    },
    [fetchMe],
  );

  const resendOtp = useCallback(async (email, guard = {}) => {
    const { data } = await api.post("/auth/resend-otp", { ...guard, email });
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* the local session is cleared regardless */
    }
    signOutLocal();
  }, [signOutLocal]);

  const value = useMemo(
    () => ({
      user,
      loading,
      status,
      isAuthenticated: status === "authenticated" && Boolean(user),
      isAdmin: status === "authenticated" && user?.role === "admin",
      login,
      register,
      verifyOtp,
      resendOtp,
      logout,
      refresh: fetchMe,
    }),
    [user, loading, status, login, register, verifyOtp, resendOtp, logout, fetchMe],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};